import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Copy, Download, Edit2, Save, ExternalLink, Calendar, StickyNote, Image as ImageIcon, Check, Trash2 } from 'lucide-react'
import ConfirmationModal from './ConfirmationModal'

interface AssetViewerModalProps {
  isOpen: boolean
  onClose: () => void
  asset: {
    id: string
    title: string
    type: 'note' | 'screenshot'
    content?: string
    imageUrl?: string
    sourceTitle?: string
    sourceUrl?: string
    createdAt?: string
    metadata?: any
  } | null
  onSave?: (id: string, updatedContent: string) => Promise<boolean>
  onDeleted?: () => void
  token?: string
}

// Safe date formatter
function formatDate(dateStr?: string): string {
  if (!dateStr) return ''
  
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return ''
    
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  } catch {
    return ''
  }
}

export default function AssetViewerModal({ isOpen, onClose, asset, onSave, onDeleted, token }: AssetViewerModalProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedContent, setEditedContent] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [copied, setCopied] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  useEffect(() => {
    if (asset?.content) {
      setEditedContent(asset.content)
    }
    // Reset states when modal opens
    setIsEditing(false)
    setSaveStatus('idle')
  }, [asset])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen || !asset) return null

  const handleCopy = async () => {
    try {
      if (asset.type === 'note') {
        await navigator.clipboard.writeText(asset.content || editedContent)
      } else if (asset.type === 'screenshot' && asset.imageUrl) {
        // For screenshots, copy the image URL or try to copy the image itself
        await navigator.clipboard.writeText(asset.imageUrl)
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleDownload = () => {
    if (asset.type === 'screenshot' && asset.imageUrl) {
      // Create a link and trigger download
      const link = document.createElement('a')
      link.href = asset.imageUrl
      link.download = `screenshot-${asset.id}.jpg`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else if (asset.type === 'note') {
      // Download note as text file
      const blob = new Blob([asset.content || editedContent], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `note-${asset.id}.txt`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  }

  const handleSave = async () => {
    if (!asset || !token) return
    
    setIsSaving(true)
    setSaveStatus('idle')
    
    try {
      const response = await fetch(`/api/content/${asset.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: editedContent })
      })
      
      if (!response.ok) {
        throw new Error('Failed to save')
      }
      
      setSaveStatus('success')
      setIsEditing(false)
      
      // Call parent onSave if provided
      if (onSave) {
        await onSave(asset.id, editedContent)
      }
      
      // Reset success status after 2 seconds
      setTimeout(() => setSaveStatus('idle'), 2000)
    } catch (err) {
      console.error('Failed to save:', err)
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 3000)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteClick = () => {
    if (!asset || !token) return
    setShowDeleteConfirm(true)
    setDeleteError(null)
  }

  const handleDelete = async () => {
    if (!asset || !token) return

    try {
      const response = await fetch(`/api/content/${asset.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) throw new Error('delete failed')

      setShowDeleteConfirm(false)
      onDeleted?.()
      onClose()
    } catch (err) {
      console.error('Failed to delete:', err)
      setDeleteError(`Failed to delete ${asset.type}. Please try again.`)
      setShowDeleteConfirm(false)
    }
  }

  const handleOpenInNewTab = () => {
    if (asset.type === 'screenshot' && asset.imageUrl) {
      const win = window.open()
      if (win) {
        win.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Screenshot - ${asset.sourceTitle || 'Capture'}</title>
              <style>
                body { margin: 0; background: #0a0a0a; display: flex; align-items: center; justify-content: center; min-height: 100vh; }
                img { max-width: 100%; max-height: 100vh; object-fit: contain; }
              </style>
            </head>
            <body>
              <img src="${asset.imageUrl}" alt="Screenshot"/>
            </body>
          </html>
        `)
      }
    }
  }

  const formattedDate = formatDate(asset.createdAt || asset.metadata?.created_at || asset.metadata?.display_date)

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex flex-col gap-2 p-4 border-b border-gray-200 dark:border-gray-700">
            {/* Attached to link - Top Left */}
            {(asset.type === 'note' || asset.type === 'screenshot') && (asset.sourceTitle || asset.metadata?.source_content_id) && (
              <div className="text-sm text-gray-900 dark:text-gray-100">
                <span className="font-medium">Attached to:</span>{' '}
                <button
                  onClick={() => {
                    const contentId = asset.metadata?.source_content_id;
                    if (contentId) {
                      window.location.href = `/library?highlight=${contentId}`;
                    }
                  }}
                  className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 hover:underline font-medium"
                >
                  {asset.sourceTitle || 'View content'}
                </button>
              </div>
            )}
            
            {/* Title and Actions Row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-2 w-2 rounded-full bg-violet-400" />
                <div>
                  <h2 className="text-sm font-medium text-gray-900 dark:text-white">
                    {asset.type === 'note' ? 'Note' : 'Screenshot'}
                  </h2>
                  {formattedDate && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {formattedDate}
                    </div>
                  )}
                </div>
              </div>

            {/* Action Buttons - Icon only like Note editor */}
            <div className="flex items-center gap-1">
              {/* Copy Button */}
              <button
                onClick={handleCopy}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="Copy to clipboard"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>

              {/* Download Button */}
              <button
                onClick={handleDownload}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="Download"
              >
                <Download className="w-4 h-4" />
              </button>

              {/* Delete Button */}
              <button
                onClick={handleDeleteClick}
                className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>

              {/* Open in New Tab (for screenshots) */}
              {asset.type === 'screenshot' && (
                <button
                  onClick={handleOpenInNewTab}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              )}

              {/* Edit/Save Button (for notes) */}
              {asset.type === 'note' && (
                <>
                  {isEditing ? (
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="p-2 rounded-lg text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50"
                      title={isSaving ? 'Saving...' : 'Save'}
                    >
                      <Save className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                </>
              )}

              {/* Close Button */}
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {asset.type === 'screenshot' ? (
              // Screenshot View
              <div className="flex flex-col items-center">
                {asset.imageUrl ? (
                  <img
                    src={asset.imageUrl}
                    alt="Screenshot"
                    className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg cursor-pointer"
                    onClick={handleOpenInNewTab}
                  />
                ) : (
                  <div className="w-full h-64 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                    <ImageIcon className="w-16 h-16 text-gray-300 dark:text-gray-600" />
                  </div>
                )}
              </div>
            ) : (
              // Note View/Editor
              <div className="h-full">
                {isEditing ? (
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full h-full min-h-[300px] p-4 text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Write your note..."
                    autoFocus
                  />
                ) : (
                  <div className="prose dark:prose-invert max-w-none">
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800/30">
                      <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
                        {asset.content || 'No content'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Save Status Indicator */}
          {saveStatus !== 'idle' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`mx-6 mb-4 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
                saveStatus === 'success' 
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
              }`}
            >
              {saveStatus === 'success' ? (
                <>
                  <Check className="w-4 h-4" />
                  Note saved successfully
                </>
              ) : (
                <>
                  <X className="w-4 h-4" />
                  Failed to save. Please try again.
                </>
              )}
              {deleteError && (
                <div className="mt-4 text-sm text-red-600 dark:text-red-400">
                  {deleteError}
                </div>
              )}
            </motion.div>
          )}
        </motion.div>
      </motion.div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false)
          setDeleteError(null)
        }}
        onConfirm={handleDelete}
        title={`Delete ${asset?.type === 'note' ? 'Note' : 'Screenshot'}`}
        message={`Are you sure you want to delete this ${asset?.type}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
      />
    </AnimatePresence>
  )
}
